/* eslint-disable no-alert */
import Grid from '@mui/material/Grid';
import PageCard from 'src/components/PageCard';
import PageTitle from 'src/components/PageTitle';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import ViewCarouselIcon from '@mui/icons-material/ViewCarousel';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import { Search } from '@mui/icons-material';
import { IconButton, Tooltip, Typography, Box, TextField } from '@mui/material';
import LoadingIconButton from 'src/components/LoadingIconButton';
import CachedOutlinedIcon from '@mui/icons-material/CachedOutlined';
import { PresetMediaItem } from 'src/pages/MachineGroupDetails/UpsertPresetForm';
import React, { useState, useCallback, useEffect } from 'react';
import {
  useDeleteMediaMutation,
  useListFoldersQuery,
  useListMediaQuery,
  useUploadMediaMutation,
  useMoveMediaToFolderMutation,
  useCreateFolderMutation,
  useDeleteFolderMutation,
  useListCustomersQuery,
  UserProfile,
  PortalRole,
} from 'src/backend';
import { useSelector } from 'react-redux';
import { getUserProfile } from 'src/components/UserStore/userProfileSlice';
import RelatedPresetsDialog from './RelatedPresetsDialog';
import UploadMediaDialog from './UploadMediaDialog';
import CreateFolderDialog, { CreateFolderRequest } from './CreateFolderDialog';
import FolderAccordion from './FolderAccordion';
import OtherMediaSection from './OtherMediaSection';

function CustomToolbar(props: {
  onSearch: (searchText: string | undefined) => void;
}) {
  const { onSearch } = props;
  const [searchText, setSearchText] = useState('');
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <TextField
        name="search"
        label="Search"
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        onKeyUp={(e) => {
          if (e.key === 'Enter') {
            onSearch(searchText === '' ? undefined : searchText);
          }
        }}
        size="small"
        sx={{ minWidth: 200 }}
      />
      <IconButton
        onClick={() => onSearch(searchText === '' ? undefined : searchText)}
      >
        <Tooltip title="Search">
          <Search />
        </Tooltip>
      </IconButton>
    </Box>
  );
}

function MediaManagementDashboard() {
  const [searchText, setSearchText] = useState<string | undefined>(undefined);
  const {
    data,
    isLoading: isMediaLoading,
    isFetching: isMediaFetching,
    refetch,
  } = useListMediaQuery(undefined);
  const userProfile = useSelector(getUserProfile) as UserProfile;
  const [createFolder, { isLoading: isCreatingFolder }] =
    useCreateFolderMutation();
  const [deleteFolder, { isLoading: isDeletingFolder }] =
    useDeleteFolderMutation();

  const { data: foldersData, refetch: refetchFolders } =
    useListFoldersQuery(undefined);

  const { data: customersData } = useListCustomersQuery(true);

  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [isCreateFolderDialogOpen, setIsCreateFolderDialogOpen] =
    useState(false);
  const [isPresetsDialogOpen, setIsPresetsDialogOpen] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState<PresetMediaItem | null>(
    null,
  );
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(
    new Set(),
  );
  const [uploadMedia, { isLoading }] = useUploadMediaMutation();
  const [deleteMedia, { isLoading: isDeletingMedia }] =
    useDeleteMediaMutation();
  const [moveMedia, { isLoading: isMovingMedia }] =
    useMoveMediaToFolderMutation();

  useEffect(() => {
    refetchFolders();
    refetch();
  }, [refetchFolders, refetch]);

  const handleDeleteMedia = useCallback(
    (mediaToDelete: PresetMediaItem) => {
      if (
        window.confirm(
          `Are you sure you want to delete "${mediaToDelete.fileName}"?`,
        )
      ) {
        deleteMedia(mediaToDelete.id)
          .unwrap()
          .then(() => {
            refetch();
          });
      }
    },
    [deleteMedia, refetch],
  );

  const handleAddMedia = useCallback(() => {
    setIsUploadDialogOpen(true);
  }, []);

  const handleUploadMedia = useCallback(
    (files: File[], customerId: string | undefined) => {
      const formData = new FormData();
      files.forEach((file) => {
        formData.append('files', file);
      });
      if (customerId) {
        formData.append('customerId', customerId);
      }
      uploadMedia(formData)
        .unwrap()
        .then(() => {
          setIsUploadDialogOpen(false);
        })
        .finally(() => {
          refetch();
        });
    },
    [uploadMedia, refetch],
  );

  const handleRefreshMedia = useCallback(() => {
    refetchFolders();
    refetch();
  }, [refetchFolders, refetch]);

  const handleShowPresets = useCallback((media: PresetMediaItem) => {
    setSelectedMedia(media);
    setIsPresetsDialogOpen(true);
  }, []);

  const handleClosePresetsDialog = useCallback(() => {
    setIsPresetsDialogOpen(false);
    setSelectedMedia(null);
  }, []);

  const handleCreateFolder = useCallback(
    (name: string, customerId: string | undefined) => {
      const createFolderRequest: CreateFolderRequest = {
        name,
        customerId,
      };
      createFolder(createFolderRequest)
        .unwrap()
        .then(() => {
          setIsCreateFolderDialogOpen(false);
          refetchFolders();
        });
    },
    [createFolder, refetchFolders],
  );

  const handleDeleteFolder = useCallback(
    (folderId: string, folderName: string) => {
      if (
        window.confirm(
          `Are you sure you want to delete folder "${folderName}"?`,
        )
      ) {
        deleteFolder(folderId)
          .unwrap()
          .then(() => {
            refetchFolders();
            refetch();
          });
      }
    },
    [deleteFolder, refetchFolders, refetch],
  );

  const handleMoveMedia = useCallback(
    (mediaId: string, folderId: string | null) => {
      const currentMedia = data?.data?.find((media) => media.id === mediaId);

      if (
        currentMedia?.folderId === folderId ||
        (currentMedia?.folderId === undefined && folderId === null)
      ) {
        return;
      }
      moveMedia({ mediaId, folderId })
        .unwrap()
        .then(() => refetch());
    },
    [moveMedia, refetch, data?.data],
  );

  const toggleFolder = useCallback((folderId: string) => {
    setExpandedFolders((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(folderId)) {
        newSet.delete(folderId);
      } else {
        newSet.add(folderId);
      }
      return newSet;
    });
  }, []);

  const onSearch = useCallback((newSearchText: string | undefined) => {
    setSearchText(newSearchText);
  }, []);

  // Group media by folders
  const groupedMedia = React.useMemo(() => {
    if (!data?.data) return { folders: [], mediaWithoutFolder: [] };

    const filteredData = searchText
      ? data.data.filter((media) =>
          media.fileName.toLowerCase().includes(searchText.toLowerCase()),
        )
      : data.data;

    const mediaByFolder: { [key: string]: PresetMediaItem[] } = {};
    const mediaWithoutFolder: PresetMediaItem[] = [];

    filteredData.forEach((media) => {
      if (media.folderId) {
        if (!mediaByFolder[media.folderId]) {
          mediaByFolder[media.folderId] = [];
        }
        mediaByFolder[media.folderId].push(media);
      } else {
        mediaWithoutFolder.push(media);
      }
    });

    // Create folder objects with media
    const folders =
      foldersData?.data?.map((folder) => ({
        ...folder,
        media: mediaByFolder[folder.id] || [],
      })) || [];

    return { folders, mediaWithoutFolder };
  }, [data?.data, foldersData?.data, searchText]);

  // Group folders by customer
  const groupedFolders = React.useMemo(() => {
    const foldersByCustomer: {
      [customerId: string]: typeof groupedMedia.folders;
    } = {};
    const unknownCustomerFolders: typeof groupedMedia.folders = [];

    const foldersToShow = searchText
      ? groupedMedia.folders.filter((folder) => folder.media.length > 0)
      : groupedMedia.folders;

    foldersToShow.forEach((folder) => {
      if (folder.customerId) {
        if (!foldersByCustomer[folder.customerId]) {
          foldersByCustomer[folder.customerId] = [];
        }
        foldersByCustomer[folder.customerId].push(folder);
      } else {
        unknownCustomerFolders.push(folder);
      }
    });

    return { foldersByCustomer, unknownCustomerFolders };
  }, [groupedMedia, searchText]);

  return (
    <>
      <UploadMediaDialog
        isOpen={isUploadDialogOpen}
        onClose={() => setIsUploadDialogOpen(false)}
        onUpload={handleUploadMedia}
        isUploading={isLoading}
      />
      <CreateFolderDialog
        isOpen={isCreateFolderDialogOpen}
        onClose={() => setIsCreateFolderDialogOpen(false)}
        onCreateFolder={handleCreateFolder}
        isCreating={isCreatingFolder}
      />
      <RelatedPresetsDialog
        isOpen={isPresetsDialogOpen}
        onClose={handleClosePresetsDialog}
        media={selectedMedia}
      />
      <PageTitle />
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <PageCard
            title="Media"
            icon={<ViewCarouselIcon />}
            sx={{
              '& .MuiCardHeader-root': {
                height: '80px',
              },
            }}
            contentSx={{ display: 'block' }}
            action={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                <IconButton onClick={handleAddMedia}>
                  <Tooltip title="Add media">
                    <AddCircleOutlineOutlinedIcon />
                  </Tooltip>
                </IconButton>
                <IconButton onClick={() => setIsCreateFolderDialogOpen(true)}>
                  <Tooltip title="Create folder">
                    <CreateNewFolderIcon />
                  </Tooltip>
                </IconButton>
                <LoadingIconButton
                  loading={false}
                  onClick={handleRefreshMedia}
                  sx={{ mr: 1 }}
                >
                  <Tooltip title="Refresh">
                    <CachedOutlinedIcon fontSize="large" />
                  </Tooltip>
                </LoadingIconButton>
                <CustomToolbar onSearch={onSearch} />
              </Box>
            }
          >
            {(isMediaLoading ||
              isMediaFetching ||
              isDeletingMedia ||
              isMovingMedia ||
              isCreatingFolder ||
              isDeletingFolder ||
              isLoading) && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: 200,
                  textAlign: 'center',
                }}
              >
                <ViewCarouselIcon
                  sx={{ fontSize: 64, color: 'grey.400', mb: 2 }}
                />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Loading media files...
                </Typography>
              </Box>
            )}

            {!isMediaLoading &&
              !isMediaFetching &&
              !isDeletingMedia &&
              !isMovingMedia &&
              !isCreatingFolder &&
              !isDeletingFolder &&
              !isLoading &&
              data?.data &&
              data?.data.length > 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {/* Folders Section */}
                  {(Object.keys(groupedFolders.foldersByCustomer).length > 0 ||
                    groupedFolders.unknownCustomerFolders.length > 0) && (
                    <Box>
                      <Typography variant="h6" sx={{ mb: 2 }}>
                        Folders
                      </Typography>

                      {/* Render folders grouped by customer */}
                      {Object.entries(groupedFolders.foldersByCustomer).map(
                        ([customerId, customerFolders]) => {
                          const customer = customersData?.data?.find(
                            (c) => c.id === customerId,
                          );
                          const customerName =
                            customer?.name || 'Unknown Customer';

                          return (
                            <Box key={customerId} sx={{ mb: 3 }}>
                              {userProfile?.role ===
                                PortalRole[PortalRole.Admin] && (
                                <Typography
                                  variant="subtitle1"
                                  sx={{
                                    mb: 1,
                                    fontWeight: 600,
                                    color: 'primary.main',
                                  }}
                                >
                                  {customerName}
                                </Typography>
                              )}

                              <Box
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: 1,
                                }}
                              >
                                {customerFolders.map((folder) => (
                                  <FolderAccordion
                                    key={folder.id}
                                    folder={folder}
                                    isExpanded={expandedFolders.has(folder.id)}
                                    onToggle={() => toggleFolder(folder.id)}
                                    onDeleteMedia={handleDeleteMedia}
                                    onShowPresets={handleShowPresets}
                                    onMoveMedia={handleMoveMedia}
                                    onDeleteFolder={handleDeleteFolder}
                                  />
                                ))}
                              </Box>
                            </Box>
                          );
                        },
                      )}

                      {/* Render folders with unknown customer */}
                      {groupedFolders.unknownCustomerFolders.length > 0 && (
                        <Box sx={{ mb: 3 }}>
                          <Typography
                            variant="subtitle1"
                            sx={{
                              mb: 1,
                              fontWeight: 600,
                              color: 'text.secondary',
                            }}
                          >
                            Unknown Customer
                          </Typography>
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 1,
                            }}
                          >
                            {groupedFolders.unknownCustomerFolders.map(
                              (folder) => (
                                <FolderAccordion
                                  key={folder.id}
                                  folder={folder}
                                  isExpanded={expandedFolders.has(folder.id)}
                                  onToggle={() => toggleFolder(folder.id)}
                                  onDeleteMedia={handleDeleteMedia}
                                  onShowPresets={handleShowPresets}
                                  onMoveMedia={handleMoveMedia}
                                  onDeleteFolder={handleDeleteFolder}
                                />
                              ),
                            )}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  )}

                  {/* Media without folder */}
                  <OtherMediaSection
                    media={groupedMedia.mediaWithoutFolder}
                    customers={customersData?.data || []}
                    onDeleteMedia={handleDeleteMedia}
                    onShowPresets={handleShowPresets}
                    onMoveMedia={handleMoveMedia}
                  />
                </Box>
              )}

            {!isMediaLoading &&
              !isMediaFetching &&
              !isCreatingFolder &&
              !isDeletingFolder &&
              !isLoading &&
              (!data?.data || data?.data.length === 0) && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: 200,
                    textAlign: 'center',
                  }}
                >
                  <ViewCarouselIcon
                    sx={{ fontSize: 64, color: 'grey.400', mb: 2 }}
                  />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No media files found
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Upload some images or videos to get started
                  </Typography>
                </Box>
              )}
          </PageCard>
        </Grid>
      </Grid>
    </>
  );
}

export default MediaManagementDashboard;
